import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  ChangeDetectorRef,
  NgZone,
} from '@angular/core'
import { CommonModule } from '@angular/common'
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms'
import {
  FullCalendarComponent,
  FullCalendarModule,
} from '@fullcalendar/angular'
import { CalendarOptions } from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import frLocale from '@fullcalendar/core/locales/fr'

// PrimeNG imports
import { CardModule } from 'primeng/card'
import { DividerModule } from 'primeng/divider'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { DatePicker, DatePickerModule } from 'primeng/datepicker'
import { ContextMenuModule } from 'primeng/contextmenu'
import { MenuItem } from 'primeng/api'

// Services, models and utilities
import { InterventionsService } from '../../services/interventions.service'
import { EventsService } from '../../../events/services/events.service'
import { UserService } from '../../../users/services/user.service'
import { DateTools } from '../../../../core/tools/date-tools'
import { CalendarTools } from '../../../../core/tools/calendar-tools'
import { AffairModel } from '../../../../core/models/affair.model'
import { UserModel } from '../../../../core/models/user.model'
import {
  BehaviorSubject,
  Observable,
  Subscription,
  finalize,
  forkJoin,
  of,
  catchError,
  combineLatest,
  map,
  startWith,
  switchMap,
  tap,
  zip,
} from 'rxjs'
import { RouteTools } from '../../../../core/tools/route-tools'
import { ToastService } from '../../../../shared/services/toast.service'

@Component({
  selector: 'app-intervention-reschedule-time-select',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FullCalendarModule,
    CardModule,
    DividerModule,
    ProgressSpinnerModule,
    DatePickerModule,
    ContextMenuModule,
    // Autres imports nécessaires
  ],
  templateUrl: './intervention-reschedule-time-select.component.html',
  styleUrls: ['./intervention-reschedule-time-select.component.scss'],
})
export class InterventionRescheduleTimeSelectComponent
  implements OnInit, OnChanges, AfterViewInit
{
  @Input() affair: AffairModel
  @Input() selectedTechnicians: UserModel[] = []
  @Input() selectedDateRange: Date[] = []
  @Input() users: any = []
  @Input() loadingUsers: boolean = false
  @Input() droppedEventDetails: any = null
  @Input() resetCalendar: boolean = false
  @Input() closestInterventions: any[] = []
  @Input() selectedProducts: any[] = []
  @Input() productGroups: any[] = []
  @Input() preSelectedSlot: any = null // Créneau pré-sélectionné depuis l'étape précédente
  @Input() excludeInterventionId: number | null = null // ID de l'intervention à exclure (celle en cours de replanification)

  @Output() dropDetails = new EventEmitter<any>()
  @Output() dateRangeChange = new EventEmitter<Date[]>()
  @Output() filtersChanged = new EventEmitter<any>()

  @ViewChild(FullCalendarComponent) calendar: FullCalendarComponent
  @ViewChild('contextMenu') contextMenu: any
  @ViewChild('weekPickerCalendar') weekPickerCalendar: DatePicker

  // Propriétés de base
  public calendarOptions: CalendarOptions
  public rangeDates = new FormControl<Date[] | null>(null)
  public filtersForm: FormGroup
  public usersForm: FormGroup

  // Propriétés pour les événements et le chargement
  public fullCalendarEvents$: Observable<any[]>
  public loadingEvents = false
  public hasDropEvent = false
  public hasSingleProductDropped = false

  // Propriétés pour la gestion des produits
  public scheduledProductIds: Set<number> = new Set()
  public currentEvents: any[] = []
  public selectedEvent: any = null
  public contextMenuItems: MenuItem[] = []

  // Propriétés pour le drag & drop
  private draggableInstance: any = null
  private processedEventIds: Set<string> = new Set()

  // Autres propriétés utilitaires
  private datesSet$ = new BehaviorSubject<any>(null)
  private subscriptions: Subscription[] = []
  public usersMultiSelectControl: FormControl

  // Subject pour gérer les événements du calendrier
  private eventsSubject = new BehaviorSubject<any[]>([])

  // Ajouter une propriété pour le technicien sélectionné pour les itinéraires
  public selectedTechForRoutes: UserModel | null = null

  // Ajouter cette propriété pour stocker la plage de dates visible du calendrier
  public currentCalendarRange: { start: Date; end: Date } = {
    start: new Date(),
    end: new Date(new Date().setDate(new Date().getDate() + 7)),
  }

  // Ajouter cette propriété pour suivre la vue actuelle du calendrier
  public currentView: string = 'timeGridWeek'

  // Ajouter les propriétés pour la plage horaire
  public timeRangeControl = new FormControl<{ start: string; end: string }>({
    start: '07:00',
    end: '20:00',
  })

  // Timer pour le debounce de la notification d'itinéraire
  private routeNotificationTimer: any = null

  constructor(
    private fb: FormBuilder,
    private interventionsService: InterventionsService,
    private eventsService: EventsService,
    private userService: UserService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private toastService: ToastService
  ) {
    // Initialisation de base
    this.initBasicProperties()
  }

  private initBasicProperties() {
    this.contextMenuItems = [
      {
        label: 'Supprimer cet événement',
        icon: 'pi pi-trash',
        command: () => {
          if (this.selectedEvent) {
            this.removeEvent(this.selectedEvent)
            this.selectedEvent = null
          }
        },
      },
    ]

    this.calendarOptions = {
      initialView: 'timeGridWeek',
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      locale: frLocale,
      editable: true,
      droppable: true,
      firstDay: 1,
      slotMinTime: '07:00:00', // Commence à 7h
      slotMaxTime: '20:00:00', // Se termine à 20h
      slotDuration: '00:15:00', // Tranches de 15 minutes
      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: 'dayGridMonth,timeGridWeek',
      },
      height: 'auto',
      datesSet: (datesSetArg) => {
        this.ngZone.run(() => {
          // Mettre à jour la plage de dates actuelle du calendrier
          this.currentCalendarRange = {
            start: datesSetArg.start,
            end: datesSetArg.end,
          }
          this.datesSet$.next(datesSetArg)
        })
      },
      drop: (info) => {
        this.ngZone.run(() => {
          this.handleEventDrop(info)
        })
      },
      eventReceive: (info) => {
        this.ngZone.run(() => {
          this.handleEventReceive(info)
        })
      },
      eventChange: (info) => {
        this.ngZone.run(() => {
          this.handleEventChange(info)
        })
      },
      eventDrop: (info) => {
        this.ngZone.run(() => {
          this.handleExistingEventDrop(info)
        })
      },
      eventResize: (info) => {
        this.ngZone.run(() => {
          this.handleExistingEventResize(info)
        })
      },
      eventMouseEnter: (info) => {
        if (
          info.event.extendedProps &&
          info.event.extendedProps['productsData']
        ) {
          this.selectedEvent = info.event
        }
      },
      eventMouseLeave: (info) => {
        if (this.selectedEvent === info.event) {
          this.selectedEvent = null
        }
      },
      eventClick: (info) => {
        this.ngZone.run(() => {
          this.handleEventClick(info)
        })
      },
      eventDidMount: (info) => {
        // Configuration des événements montés
        this.configureEventDisplay(info)
      },
      // Ajouter un handler pour mettre à jour la vue actuelle quand elle change
      viewDidMount: (info) => {
        this.ngZone.run(() => {
          this.currentView = info.view.type
        })
      },
    }
  }

  ngOnInit() {
    this.initForms()
    this.setupEventListeners()
    this.setupCalendarEvents()
    this.setupUsers()

    // Initialiser les éléments draggable après un court délai
    setTimeout(() => {
      this.initializeDraggable()
    }, 100)
  }

  ngOnChanges(changes: SimpleChanges) {
    // Handle date range changes
    if (
      changes['selectedDateRange'] &&
      changes['selectedDateRange'].currentValue &&
      changes['selectedDateRange'].currentValue.length === 2
    ) {
      const newDates = changes['selectedDateRange'].currentValue.map(
        (d: any) => new Date(d)
      )
      this.rangeDates.setValue(newDates, { emitEvent: false })

      if (this.calendar?.getApi()) {
        const api = this.calendar.getApi()
        api.gotoDate(newDates[0])
        this.updateCalendarViewBasedOnDateRange(newDates)
      }
    }

    // Handle technician changes
    if (
      changes['selectedTechnicians'] &&
      changes['selectedTechnicians'].currentValue
    ) {
      if (this.usersForm) {
        this.usersForm
          .get('users')
          ?.setValue(changes['selectedTechnicians'].currentValue)
      }

      // Automatically select the first technician for routes when the list changes
      if (this.selectedTechnicians?.length > 0) {
        this.selectedTechForRoutes = this.selectedTechnicians[0]
      } else {
        this.selectedTechForRoutes = null
      }
    }

    // Handle dropped event details
    if (
      changes['droppedEventDetails'] &&
      changes['droppedEventDetails'].currentValue
    ) {
      this.processDroppedEventDetails(
        changes['droppedEventDetails'].currentValue
      )
    }

    // Handle calendar reset
    if (
      changes['resetCalendar'] &&
      changes['resetCalendar'].currentValue === true
    ) {
      this.clearCalendarEvents()
      this.hasDropEvent = false
      this.dropDetails.emit(null)
    }

    // Handle pre-selected slot from previous step
    if (changes['preSelectedSlot']) {
      const newSlot = changes['preSelectedSlot'].currentValue
      if (newSlot && !this.hasDropEvent) {
        // Appliquer le créneau pré-sélectionné après un court délai pour s'assurer que le calendrier est prêt
        setTimeout(() => {
          this.applyPreSelectedSlot(newSlot)
        }, 500)
      }
    }
  }

  ngAfterViewInit() {
    this.initializeCalendarAfterView()
  }

  // Initialisation de formulaires
  private initForms() {
    this.filtersForm = this.fb.group({
      departmentsCode: [[]],
      search: [''],
    })

    this.usersForm = this.fb.group({
      users: this.fb.control<UserModel[]>([]),
    })

    if (this.selectedTechnicians.length) {
      this.usersForm.get('users')?.setValue(this.selectedTechnicians)
    }

    if (this.selectedDateRange && this.selectedDateRange.length === 2) {
      this.rangeDates.setValue([
        new Date(this.selectedDateRange[0]),
        new Date(this.selectedDateRange[1]),
      ])
    }
  }

  private setupEventListeners() {
    // Configuration des événements de formulaire et autres écouteurs
    this.rangeDates.valueChanges.subscribe((dates) => {
      if (dates && dates.length === 2 && DateTools.isValidDateRange(dates)) {
        try {
          const newStartDate = DateTools.toValidDate(dates[0])
          const newEndDate = DateTools.toValidDate(dates[1])

          if (newStartDate && newEndDate) {
            const normalizedStart = this.normalizeDate(newStartDate, true)
            const normalizedEnd = this.normalizeDate(newEndDate, false)

            this.dateRangeChange.emit([normalizedStart, normalizedEnd])
            this.updateCalendarViewBasedOnDateRange([
              normalizedStart,
              normalizedEnd,
            ])
          }
        } catch (error) {
          console.error('Error processing date range change:', error)
        }
      }
    })

    // Ajouter l'écoute des changements de plage horaire
    this.timeRangeControl.valueChanges.subscribe((timeRange) => {
      if (timeRange && this.calendar?.getApi()) {
        this.updateCalendarTimeRange(timeRange.start, timeRange.end)
      }
    })
  }

  private setupCalendarEvents() {
    this.fullCalendarEvents$ = combineLatest([
      this.datesSet$.pipe(startWith(null)),
      this.usersForm.valueChanges.pipe(
        startWith({ users: this.selectedTechnicians })
      ),
    ]).pipe(
      switchMap(([datesSet, usersFormValue]) => {
        const start = datesSet
          ? datesSet.start
          : this.selectedDateRange && this.selectedDateRange.length > 0
            ? this.selectedDateRange[0]
            : new Date()

        const end = datesSet
          ? datesSet.end
          : this.selectedDateRange && this.selectedDateRange.length > 1
            ? this.selectedDateRange[1]
            : new Date(new Date().setDate(new Date().getDate() + 7))

        // Récupérer les IDs des techniciens sélectionnés
        const technicianIds =
          this.selectedTechnicians?.map((tech) => tech.id) || []

        // Si aucun technicien n'est sélectionné, retourner un tableau vide
        if (technicianIds.length === 0) {
          return of([])
        }

        const params = {
          users: technicianIds,
          dateFrom: DateTools.formatDateForAPI(start),
          dateTo: DateTools.formatDateForAPI(end),
        }

        return this.getEventsAndInterventionsForCalendar(params)
      }),
      // Stocker les événements actuels chaque fois qu'ils sont mis à jour
      tap((events) => (this.currentEvents = events))
    )

    // Souscrire à l'Observable pour s'assurer que les événements sont chargés
    this.subscriptions.push(this.fullCalendarEvents$.subscribe())
  }

  /**
   * Force le rafraîchissement des interventions et événements depuis l'API
   * Cette méthode recharge les données sans préserver les événements créés par l'utilisateur
   */
  public forceRefreshEvents(): void {
    const start =
      this.selectedDateRange && this.selectedDateRange.length > 0
        ? this.selectedDateRange[0]
        : new Date()
    const end =
      this.selectedDateRange && this.selectedDateRange.length > 1
        ? this.selectedDateRange[1]
        : new Date(new Date().setDate(new Date().getDate() + 7))
    const users =
      this.selectedTechnicians?.map((user: UserModel) => user.id) || []
    const params = {
      users,
      dateFrom: DateTools.formatDateForAPI(start),
      dateTo: DateTools.formatDateForAPI(end),
    }
    this.loadingEvents = true
    this.cdr.detectChanges()

    this.getEventsAndInterventionsForCalendar(params).subscribe({
      next: (apiEvents) => {
        console.log("Événements rafraîchis depuis l'API:", apiEvents)

        // Récupérer les événements utilisateur pour les préserver
        const existingUserEvents = this.preserveUserCreatedEvents()

        // Combiner les événements de l'API avec les événements utilisateur préservés
        const allEvents = [...apiEvents, ...existingUserEvents]
        this.currentEvents = allEvents

        // Émettre les événements via le BehaviorSubject
        this.eventsSubject.next(allEvents)

        // Mettre à jour le calendrier manuellement
        if (this.calendar?.getApi()) {
          const calendarApi = this.calendar.getApi()
          // Supprimer uniquement les événements non-utilisateur
          calendarApi.getEvents().forEach((event) => {
            if (!event.classNames.includes('user-created-event')) {
              event.remove()
            }
          })
          // Ajouter les nouveaux événements de l'API
          apiEvents.forEach((eventData: any) => {
            calendarApi.addEvent(eventData)
          })
        }

        this.loadingEvents = false
        this.cdr.detectChanges()

        this.toastService.showSuccess('Données actualisées avec succès')
      },
      error: (error) => {
        console.error('Erreur lors du rafraîchissement des événements:', error)
        this.loadingEvents = false
        this.cdr.detectChanges()
        this.toastService.showError(
          "Erreur lors de l'actualisation des données"
        )
      },
    })
  }

  /**
   * Preserve user-created events (including pre-selected ones) during API reloads
   */
  private preserveUserCreatedEvents(): any[] {
    if (!this.calendar?.getApi()) {
      return []
    }

    const calendarApi = this.calendar.getApi()
    const allEvents = calendarApi.getEvents()

    // Find user-created or pre-selected events
    const userEvents = allEvents.filter(
      (event) =>
        event.classNames.includes('user-created-event') ||
        event.classNames.includes('preselected-event') ||
        event.extendedProps?.['isPreSelected'] ||
        event.extendedProps?.['preserveOnReload']
    )

    // Convert to plain objects that can be re-added to the calendar
    return userEvents.map((event) => ({
      id: event.id,
      title: event.title,
      start: event.start,
      end: event.end,
      classNames: event.classNames,
      editable: true,
      extendedProps: event.extendedProps,
    }))
  }

  private setupUsers() {
    this.usersMultiSelectControl = this.usersForm.get('users') as FormControl
    this.prepareUserData()
    this.updateAvailableTechnicians()
  }

  // MÉTHODE MANQUANTE: Configurer l'affichage des événements
  private configureEventDisplay(info: any) {
    // Rendre modifiables uniquement les événements créés par l'utilisateur
    if (info.event.classNames.includes('user-created-event')) {
      // Rendre cet événement spécifique modifiable
      info.event.setProp('editable', true)
    } else {
      // Ajouter une classe pour indiquer que l'événement est non modifiable
      info.el.classList.add('non-editable-event')
    }

    // Vérifier si cet événement correspond à une intervention proche
    if (this.closestInterventions && this.closestInterventions.length > 0) {
      const matchingIntervention = this.closestInterventions.find(
        (intervention) =>
          intervention.id === info.event.extendedProps['data']?.id
      )

      if (matchingIntervention) {
        // Utiliser des attributs de données et des classes au lieu de créer des éléments DOM
        info.el.classList.add('nearby-intervention')

        // Stocker les données comme attributs pour le styling CSS
        info.el.setAttribute(
          'data-distance',
          matchingIntervention.distance?.toString() || '??'
        )

        // Obtenir les initiales du technicien
        const technician =
          matchingIntervention.usersTechnicians &&
          matchingIntervention.usersTechnicians.length > 0
            ? matchingIntervention.usersTechnicians[0]
            : null

        const technicianInitials = technician
          ? this.getTechnicianInitials(technician)
          : '??'
        info.el.setAttribute('data-tech-initials', technicianInitials)
      }
    }
  }

  // MÉTHODE MANQUANTE: Initialiser le calendrier après l'affichage
  private initializeCalendarAfterView() {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.ngZone.run(() => {
          if (this.calendar?.getApi() && this.selectedDateRange.length === 2) {
            this.updateCalendarViewBasedOnDateRange(this.selectedDateRange)

            // Ne pas restaurer automatiquement l'événement déposé
            // Pour forcer l'utilisateur à faire un glisser-déposer manuel

            this.cdr.detectChanges()
          }
        })
      }, 0)
    })

    // Re-initialiser draggable après le chargement du contenu
    setTimeout(() => {
      if (document.querySelectorAll('.product-group-item').length > 0) {
        this.initializeDraggable()
      }
    }, 1000)
  }

  // MÉTHODE MANQUANTE: Traiter les détails de l'événement déposé
  private processDroppedEventDetails(eventDetails: any) {
    // Ne pas restaurer automatiquement l'événement - nous voulons un glisser-déposer manuel
    // On stocke les détails mais on ne crée pas l'événement
    this.droppedEventDetails = eventDetails

    // Indiquer que nous avons un événement à glisser-déposer, mais il n'est pas encore sur le calendrier
    this.hasDropEvent = false

    // Émettre les détails pour informer le parent que nous avons les données mais qu'elles
    // nécessitent une action de l'utilisateur
    this.dropDetails.emit({
      ...eventDetails,
      isAlreadyInCalendar: false,
      requireDragDrop: true,
    })

    console.log(
      'Événement nécessitant un glisser-déposer manuel - ne pas ajouter automatiquement'
    )
  }

  // MÉTHODE MANQUANTE: Mettre à jour la vue du calendrier en fonction de la plage de dates
  updateCalendarViewBasedOnDateRange(dateRange: Date[]) {
    if (!dateRange || dateRange.length < 2 || !this.calendar?.getApi()) return

    this.ngZone.runOutsideAngular(() => {
      const calendarApi = this.calendar.getApi()
      const startDate = new Date(dateRange[0])
      const endDate = new Date(dateRange[1])
      const diffDays = Math.ceil(
        Math.abs(endDate.getTime() - startDate.getTime()) /
          (1000 * 60 * 60 * 24)
      )

      // Choisir la vue appropriée en fonction de la plage de dates
      calendarApi.changeView(diffDays <= 7 ? 'timeGridWeek' : 'dayGridMonth')
      calendarApi.gotoDate(startDate)

      setTimeout(() => {
        this.ngZone.run(() => {
          this.cdr.detectChanges()
        })
      }, 0)
    })
  }

  /**
   * Met à jour la plage horaire du calendrier
   * @param startTime Heure de début (format HH:mm)
   * @param endTime Heure de fin (format HH:mm)
   */
  updateCalendarTimeRange(startTime: string, endTime: string): void {
    if (!this.calendar?.getApi()) return

    const calendarApi = this.calendar.getApi()

    // Mettre à jour les options du calendrier
    calendarApi.setOption('slotMinTime', `${startTime}:00`)
    calendarApi.setOption('slotMaxTime', `${endTime}:00`)

    // Forcer le rafraîchissement du calendrier
    calendarApi.render()
  }

  /**
   * Obtient la plage horaire actuelle
   * @returns Objet contenant les heures de début et fin
   */
  getCurrentTimeRange(): { start: string; end: string } {
    return this.timeRangeControl.value || { start: '07:00', end: '20:00' }
  }

  /**
   * Définit une plage horaire prédéfinie
   * @param start Heure de début
   * @param end Heure de fin
   */
  setTimeRange(start: string, end: string): void {
    this.timeRangeControl.setValue({ start, end })
  }

  /**
   * Réinitialise la plage horaire par défaut
   */
  resetTimeRange(): void {
    this.setTimeRange('07:00', '20:00')
  }

  // MÉTHODE MANQUANTE: Récupérer les événements et interventions pour le calendrier
  getEventsAndInterventionsForCalendar(params: {
    users: number[]
    dateFrom: string
    dateTo: string
  }) {
    this.loadingEvents = true
    return zip([
      this.interventionsService.getInterventions(params),
      this.eventsService.getEvents(params),
    ]).pipe(
      finalize(() => (this.loadingEvents = false)),
      map(([interventions, events]) => {
        // Filtrer l'intervention en cours de replanification pour éviter les doublons
        let filteredInterventions = interventions
        if (this.excludeInterventionId) {
          console.log(
            `Filtrage de l'intervention ID: ${this.excludeInterventionId}`
          )
          filteredInterventions = interventions.filter((intervention: any) => {
            const interventionId =
              intervention.data?.id ||
              intervention.id ||
              intervention.extendedProps?.data?.id
            const shouldExclude = interventionId === this.excludeInterventionId
            if (shouldExclude) {
              console.log(
                `Intervention exclue:`,
                interventionId,
                intervention.title
              )
            }
            return !shouldExclude
          })
          console.log(
            `Interventions après filtrage: ${filteredInterventions.length}/${interventions.length}`
          )
        }

        // Normaliser les dates des événements en objets Date JavaScript
        const normalizedEvents = [...filteredInterventions, ...events].map(
          (event) => {
            if (event.start && typeof event.start === 'string') {
              // Convertir les chaînes de date en objets Date
              event.start = this.parseApiDateString(event.start)
            }
            if (event.end && typeof event.end === 'string') {
              // Convertir les chaînes de date en objets Date
              event.end = this.parseApiDateString(event.end)
            }

            // Ajouter la classe "closest-intervention-event" aux événements des techniciens
            if (event.data?.resource === 'intervention') {
              event.classNames = ['closest-intervention-event']

              // Ajout des informations pour l'affichage des initiales et distances
              if (
                event.data.usersTechnicians &&
                event.data.usersTechnicians.length > 0
              ) {
                const technician = event.data.usersTechnicians[0]
                event.technicianInitials =
                  this.getTechnicianInitials(technician)
                event.technicianColor = technician.color || '#666'
              }

              // Si c'est l'une des interventions proches, ajouter la distance
              if (
                this.closestInterventions &&
                this.closestInterventions.length > 0
              ) {
                const matchingIntervention = this.closestInterventions.find(
                  (intervention) => intervention.id === event.data.id
                )
                if (matchingIntervention) {
                  event.distance = matchingIntervention.distance
                }
              }
            }

            return event
          }
        )
        return normalizedEvents
      })
    )
  }

  // MÉTHODE MANQUANTE: Traiter une chaîne de date de l'API
  private parseApiDateString(dateStr: string): Date {
    if (!dateStr) return new Date()

    // Si la chaîne contient déjà un 'T' et 'Z', c'est probablement déjà un format ISO
    if (dateStr.includes('T')) {
      return new Date(dateStr)
    }

    // Format "YYYY-MM-DD HH:mm:ss"
    const [datePart, timePart] = dateStr.split(' ')
    if (datePart && timePart) {
      const [year, month, day] = datePart.split('-').map(Number)
      const [hours, minutes, seconds] = timePart.split(':').map(Number)

      // Mois en JavaScript commence à 0, donc on soustrait 1
      return new Date(year, month - 1, day, hours, minutes, seconds)
    }

    // Fallback
    return new Date(dateStr)
  }

  // MÉTHODE MANQUANTE: Normaliser une date (début ou fin)
  private normalizeDate(date: Date, isStartDate: boolean): Date {
    const normalizedDate = new Date(date)
    if (isStartDate) {
      normalizedDate.setHours(0, 0, 0, 0) // Début de journée
    } else {
      normalizedDate.setHours(23, 59, 59, 999) // Fin de journée
    }
    return normalizedDate
  }

  // MÉTHODE MANQUANTE: Préparer les données des utilisateurs
  prepareUserData() {
    if (!this.users || !this.users.length) return

    this.users.forEach((user: any) => {
      user.fullName =
        user.firstname && user.lastname
          ? `${user.firstname} ${user.lastname}`
          : 'Technicien'
    })
  }

  // MÉTHODE MANQUANTE: Mettre à jour les techniciens disponibles
  updateAvailableTechnicians() {
    // Cette méthode est appelée mais n'est pas nécessaire dans ce contexte
    // car nous ne montrons pas la liste des techniciens disponibles
  }

  // MÉTHODE MANQUANTE: Obtenir les initiales d'un technicien
  getTechnicianInitials(technician: any): string {
    if (technician.initials) return technician.initials
    if (!technician.firstname || !technician.lastname) return '??'
    return (
      technician.firstname.charAt(0).toUpperCase() +
      technician.lastname.charAt(0).toUpperCase()
    )
  }

  // MÉTHODE MANQUANTE: Initialiser les éléments glissables
  initializeDraggable() {
    this.ngZone.run(() => {
      setTimeout(() => {
        const container = document.getElementById('external-events')
        if (!container) {
          console.error('Container external-events not found')
          return
        }

        console.log('Initializing draggable elements')
        // Créer une nouvelle instance draggable
        this.draggableInstance = CalendarTools.initializeProductDraggable(
          container,
          this.selectedProducts,
          (el: any) => this.prepareDragEventData(el)
        )

        console.log('Draggable initialized!')
      }, 100)
    })
  }

  // MÉTHODE MANQUANTE: Préparer les données pour l'élément glissable
  private prepareDragEventData(el: HTMLElement): any {
    if (
      el.classList.contains('disabled-product') ||
      this.hasSingleProductDropped
    ) {
      return null
    }

    if (el.classList.contains('product-item')) {
      return this.prepareSingleProductDragData(el)
    } else {
      return this.prepareGroupProductsDragData(el)
    }
  }

  // MÉTHODE MANQUANTE: Préparer les données pour un produit unique
  private prepareSingleProductDragData(el: HTMLElement): any {
    const productId = el.getAttribute('data-product')
    const productData = this.selectedProducts.find(
      (p: any) => p.id == productId
    )
    const durationMinutes = productData?.product.duration || 0

    return {
      title: productData?.product.name || '',
      duration: { seconds: durationMinutes * 60 },
      extendedProps: {
        productsData: [productData],
        totalDurationMinutes: durationMinutes,
      },
      classNames: ['custom-event-drag'],
      html: `<div class="fc-event-title">${productData?.product.name || ''}</div>
             <div class="event-duration-badge">${durationMinutes} min</div>`,
    }
  }

  // MÉTHODE MANQUANTE: Préparer les données pour un groupe de produits
  private prepareGroupProductsDragData(el: HTMLElement): any {
    const productIdsStr = el.getAttribute('data-products') || ''
    const isManualGroup = el.getAttribute('data-is-manual') === 'true'
    const groupId = el.getAttribute('data-group-id')

    if (!productIdsStr) {
      return {
        title: 'Produits groupés',
        duration: { seconds: 3600 },
        extendedProps: { productsData: [] },
      }
    }

    const productIds = productIdsStr.split(',').map((id) => Number(id.trim()))
    const productsData = this.selectedProducts.filter((p: any) =>
      productIds.includes(p.id)
    )
    const totalDurationMinutes = productsData.reduce(
      (sum: number, p: any) => sum + (p.product.duration || 0),
      0
    )

    const productsNames = this.formatGroupName(productsData)
    const groupName = productsNames || 'Produits groupés'

    return {
      title: groupName,
      duration: { seconds: totalDurationMinutes * 60 },
      extendedProps: {
        productsData: productsData,
        totalDurationMinutes: totalDurationMinutes,
        isManualGroup: isManualGroup,
        groupId: groupId,
      },
      classNames: ['custom-event-drag', 'group-event'],
      html: `<div class="fc-event-title">${groupName}</div>
             <div class="event-duration-badge">${totalDurationMinutes} min</div>`,
    }
  }

  // MÉTHODE MANQUANTE: Formater le nom du groupe
  private formatGroupName(products: any[]): string {
    return (
      products
        .map((p) => p.product?.name || 'Produit')
        .join(', ')
        .substring(0, 60) + (products.length > 2 ? '...' : '')
    )
  }

  // MÉTHODE MANQUANTE: Obtenir la source ERP par défaut
  private getDefaultErpSource(): string {
    return this.selectedTechnicians && this.selectedTechnicians.length > 0
      ? this.selectedTechnicians[0].erpSource
      : 'mylink'
  }

  // MÉTHODE MANQUANTE: Définir la source ERP sur les produits
  private setErpSourceOnProducts(products: any[], erpSource: string) {
    if (!products) return

    products.forEach((product: any) => {
      if (!product.erpSource) {
        product.erpSource = erpSource
      }
    })
  }

  // MÉTHODE MANQUANTE: Ajouter des produits à la liste des programmés
  private addProductsToScheduled(products: any[]) {
    if (!products) return

    const productIds = products.map((p: any) => p.id)
    productIds.forEach((id) => this.scheduledProductIds.add(Number(id)))
  }

  // MÉTHODE MANQUANTE: Calculer la durée d'un événement
  private calculateEventDuration(event: any): number {
    return (
      event.extendedProps.totalDurationMinutes ||
      event.extendedProps.productsData?.reduce(
        (sum: number, product: any) => sum + (product.product.duration || 0),
        0
      ) ||
      60
    )
  }

  // MÉTHODE MANQUANTE: Calculer l'heure de fin d'un événement
  private calculateEventEndTime(
    startTime: Date,
    durationMinutes: number
  ): Date {
    const endTime = new Date(startTime.getTime())
    endTime.setMinutes(endTime.getMinutes() + durationMinutes)
    return endTime
  }

  // MÉTHODE MANQUANTE: Vider les événements du calendrier
  clearCalendarEvents(): void {
    if (this.calendar?.getApi()) {
      const calendarApi = this.calendar.getApi()
      calendarApi.getEvents().forEach((event) => event.remove())
      this.scheduledProductIds.clear()
      this.hasSingleProductDropped = false
    }
  }

  // MÉTHODE MANQUANTE: Vérifier si un produit est programmé
  isProductScheduled(productId: number): boolean {
    return this.scheduledProductIds.has(Number(productId))
  }

  // MÉTHODE MANQUANTE: Obtenir les produits non programmés
  get unscheduledProducts(): any[] {
    return (this.selectedProducts || []).filter(
      (p: any) => !this.isProductScheduled(p.id)
    )
  }

  // MÉTHODE MANQUANTE: Obtenir les groupes de produits non programmés
  get unscheduledProductGroups(): any[] {
    if (!this.productGroups || this.productGroups.length === 0) {
      return []
    }

    return this.productGroups.filter(
      (group) =>
        !group.products.some((product: any) =>
          this.isProductScheduled(product.id)
        )
    )
  }

  // MÉTHODE MANQUANTE: Obtenir la chaîne d'IDs de produits
  getProductIdsString(products: any[]): string {
    if (!products || !Array.isArray(products) || products.length === 0) {
      return ''
    }

    return products
      .map((product) => product?.id?.toString() || '')
      .filter((id) => id)
      .join(',')
  }

  // MÉTHODE MANQUANTE: Supprimer un utilisateur
  removeUser(user: UserModel) {
    const selectedUsers = this.usersForm.controls['users'].value ?? []
    this.usersForm.controls['users'].setValue(
      selectedUsers.filter((u: any) => u.id !== user.id)
    )
  }

  // MÉTHODE MANQUANTE: Gérer le dépôt d'un événement
  handleEventDrop(info: any) {
    console.log('Event dropped:', info)
    this.hasDropEvent = true
    setTimeout(() => {
      CalendarTools.clearDuplicateEvents(this.calendar?.getApi())
    }, 200)
  }

  // MÉTHODE MANQUANTE: Gérer la réception d'un événement
  handleEventReceive(info: any) {
    const event = info.event
    const productIds =
      event.extendedProps.productsData?.map((p: any) => p.id) || []
    const eventId = DateTools.generateEventId(productIds)

    // Vérifier les duplications
    const productIdSignature = DateTools.createProductSignature(productIds)
    if (this.processedEventIds.has(productIdSignature)) {
      console.log('Duplication détectée pour', productIdSignature, '- ignorer')
      event.remove()
      return
    }

    // Ajouter aux événements traités et effacer après un délai
    this.processedEventIds.add(productIdSignature)
    this.processedEventIds.add(eventId) // Ajouter aussi l'eventId pour éviter les doublons de notification
    setTimeout(() => {
      this.processedEventIds.clear()
    }, 500)

    // Mettre à jour l'état
    this.hasDropEvent = true
    this.droppedEventDetails = null
    const isProductGroup = event.extendedProps?.productsData?.length > 1
    this.hasSingleProductDropped = !isProductGroup

    // Définir les propriétés de l'événement
    event.setProp('classNames', ['user-created-event'])
    // Rendre explicitement l'événement modifiable
    event.setProp('editable', true)
    // Obtenir la source ERP des techniciens
    const erpSource = this.getDefaultErpSource()
    // Définir la source ERP sur tous les produits
    this.setErpSourceOnProducts(event.extendedProps.productsData, erpSource)
    // Ajouter les IDs de produit à la liste des programmés
    this.addProductsToScheduled(event.extendedProps.productsData)

    // Calculer la durée et définir l'heure de fin
    const totalDurationMinutes = this.calculateEventDuration(event)

    // S'assurer que la date de début est correctement formatée
    const startTime = new Date(event.start)

    // Calculer la date de fin correcte
    const endTime = this.calculateEventEndTime(startTime, totalDurationMinutes)

    // Appliquer la date de fin à l'événement
    event.setEnd(endTime)

    // Créer l'objet de détails de dépôt
    const dropDetails = {
      title: event.title,
      date: event.start,
      productsData: event.extendedProps.productsData,
      duration: totalDurationMinutes,
      end: endTime,
      erpSource: erpSource,
      isProductGroup: isProductGroup,
      isAlreadyInCalendar: true,
      uniqueId: eventId,
    }

    // Définir un ID unique sur l'événement
    event.setExtendedProp('uniqueId', eventId)

    // Ajouter l'événement créé à currentEvents pour le calcul de l'itinéraire
    const newEventData = {
      id: eventId,
      title: event.title,
      start: event.start,
      end: endTime,
      classNames: ['user-created-event'],
      isUserCreated: true,
      productsData: event.extendedProps.productsData,
      extendedProps: {
        productsData: event.extendedProps.productsData,
        uniqueId: eventId,
      },
    }
    this.currentEvents.push(newEventData)

    // Émettre les détails de dépôt
    this.ngZone.run(() => {
      this.dropDetails.emit(dropDetails)
      this.cdr.detectChanges()
    })

    // Notifier l'utilisateur que l'itinéraire a été recalculé
    this.notifyRouteRecalculated()
  }

  // MÉTHODE MANQUANTE: Gérer le changement d'un événement
  handleEventChange(info: any) {
    const event = info.event

    // Ignorer si c'est un événement qui vient d'être créé (handleEventReceive s'en occupe)
    // FullCalendar déclenche parfois eventChange juste après eventReceive
    if (this.processedEventIds.has(event.extendedProps?.uniqueId || event.id)) {
      return
    }

    console.log('Event moved or modified:', event)

    const updatedDetails = {
      title: event.title,
      date: event.start,
      productsData: event.extendedProps.productsData,
      duration: event.duration,
      end: event.end,
    }

    // Mettre à jour l'événement dans currentEvents
    const uniqueId = event.extendedProps?.uniqueId || event.id
    const eventIndex = this.currentEvents.findIndex(
      (e) =>
        e.id === uniqueId ||
        e.extendedProps?.uniqueId === uniqueId ||
        (e.isUserCreated &&
          this.isSameDay(new Date(e.start), new Date(event.start)))
    )

    if (eventIndex !== -1) {
      // Mettre à jour les dates de l'événement existant
      this.currentEvents[eventIndex] = {
        ...this.currentEvents[eventIndex],
        start: event.start,
        end: event.end,
      }
    }

    this.ngZone.run(() => {
      this.dropDetails.emit(updatedDetails)
      this.cdr.detectChanges()
    })

    // Notifier l'utilisateur que l'itinéraire a été recalculé
    this.notifyRouteRecalculated()
  }

  /**
   * Handle drag and drop of existing events (interventions/events from the API)
   * Similar to the calendar component's handleEventDrop
   */
  handleExistingEventDrop(info: any): void {
    const { event } = info

    // Skip user-created events - they are handled by handleEventChange
    if (event.classNames.includes('user-created-event')) {
      return
    }

    const eventData = event.extendedProps?.data
    if (!eventData) {
      info.revert()
      return
    }

    const interventionId = eventData.id
    const erpSource = eventData.erpSource
    const resource = eventData.resource

    const startDate = DateTools.toLocalISOString(event.start)
    const endDate = DateTools.toLocalISOString(event.end)
    const duration = DateTools.calcTimeDifference(
      new Date(startDate),
      new Date(endDate)
    )

    this.loadingEvents = true

    const params = {
      users: this.selectedTechnicians?.map((user) => user.id) ?? [],
      startDate: startDate,
      erpSource: erpSource,
      duration: duration.toTimeString(),
    }

    if (resource === 'intervention') {
      this.interventionsService
        .updateDate(interventionId, params)
        .pipe(finalize(() => (this.loadingEvents = false)))
        .subscribe({
          next: () => {
            this.toastService.showSuccess(
              'Intervention mise à jour avec succès'
            )
            // Mettre à jour les données de l'événement dans currentEvents pour garder la synchronisation
            this.updateEventInCurrentEvents(
              interventionId,
              event.start,
              event.end
            )
            // Notifier l'utilisateur que l'itinéraire a été recalculé
            this.notifyRouteRecalculated()
          },
          error: (err) => {
            this.toastService.showError(
              "Erreur lors de la mise à jour de l'intervention"
            )
            info.revert()
          },
        })
    } else if (resource === 'event') {
      this.eventsService
        .updateDate(interventionId, params)
        .pipe(finalize(() => (this.loadingEvents = false)))
        .subscribe({
          next: () => {
            this.toastService.showSuccess('Événement mis à jour avec succès')
            // Mettre à jour les données de l'événement dans currentEvents pour garder la synchronisation
            this.updateEventInCurrentEvents(
              interventionId,
              event.start,
              event.end
            )
            // Notifier l'utilisateur que l'itinéraire a été recalculé
            this.notifyRouteRecalculated()
          },
          error: (err) => {
            this.toastService.showError(
              "Erreur lors de la mise à jour de l'événement"
            )
            info.revert()
          },
        })
    } else {
      this.loadingEvents = false
      info.revert()
    }
  }

  /**
   * Handle resize of existing events (interventions/events from the API)
   * Reuses the same logic as handleExistingEventDrop
   */
  handleExistingEventResize(info: any): void {
    // Reuse the same logic as drop
    this.handleExistingEventDrop(info)
  }

  /**
   * Met à jour les données d'un événement dans currentEvents après un déplacement réussi
   * Cela permet de garder la synchronisation locale sans recharger depuis l'API
   */
  private updateEventInCurrentEvents(
    eventId: number,
    newStart: Date,
    newEnd: Date
  ): void {
    const eventIndex = this.currentEvents.findIndex(
      (e) => e.extendedProps?.data?.id === eventId
    )

    if (eventIndex !== -1) {
      // Mettre à jour les dates dans le tableau local
      this.currentEvents[eventIndex] = {
        ...this.currentEvents[eventIndex],
        start: newStart,
        end: newEnd,
      }

      // Mettre à jour également les données internes si elles existent
      if (this.currentEvents[eventIndex].extendedProps?.data) {
        this.currentEvents[eventIndex].extendedProps.data.startDate = newStart
        this.currentEvents[eventIndex].extendedProps.data.endDate = newEnd
      }
    }
  }

  /**
   * Notifie l'utilisateur que l'itinéraire a été recalculé et l'incite à cliquer sur le bouton dédié
   * Utilise un debounce pour éviter les notifications multiples
   */
  notifyRouteRecalculated(): void {
    // Annuler le timer précédent s'il existe
    if (this.routeNotificationTimer) {
      clearTimeout(this.routeNotificationTimer)
    }

    // Déclencher la notification après un court délai (debounce)
    this.routeNotificationTimer = setTimeout(() => {
      this.toastService.showInfo(
        "Itinéraire recalculé ! Cliquez sur les boutons d'itinéraire au-dessus du calendrier pour le consulter."
      )
      this.routeNotificationTimer = null
    }, 300)
  }

  // MÉTHODE MANQUANTE: Gérer le clic sur un événement
  handleEventClick(clickInfo: any) {
    // Empêcher l'action par défaut (navigation)
    clickInfo.jsEvent.preventDefault()

    // Cette méthode pourrait être utilisée pour afficher une fenêtre modale avec les détails
    // de l'événement, mais nous ne l'implémentons pas ici pour rester simple

    console.log('Event clicked:', clickInfo.event)
  }

  // MÉTHODE MANQUANTE: Supprimer un événement
  removeEvent(event: any) {
    // Récupérer les IDs de produit
    const productIds =
      event.extendedProps?.productsData?.map((p: any) => p.id) || []

    // Supprimer les produits de la liste des programmés
    productIds.forEach((id: number) =>
      this.scheduledProductIds.delete(Number(id))
    )

    // Supprimer l'événement de currentEvents pour mettre à jour l'itinéraire
    const uniqueId = event.extendedProps?.uniqueId || event.id
    this.currentEvents = this.currentEvents.filter(
      (e) =>
        e.id !== uniqueId &&
        e.extendedProps?.uniqueId !== uniqueId &&
        !(e.isUserCreated && e.start?.getTime?.() === event.start?.getTime?.())
    )

    // Supprimer l'événement du calendrier
    event.remove()

    // Réinitialiser l'état
    this.hasSingleProductDropped = false
    this.hasDropEvent = false
    this.droppedEventDetails = null

    // Notifier le parent
    this.dropDetails.emit(null)

    // Mettre à jour l'UI
    this.cdr.detectChanges()
  }

  /**
   * Compte le nombre d'interventions pour un jour et un technicien donnés
   * @param date La date à vérifier
   * @param technician Le technicien à vérifier
   * @returns Le nombre d'interventions
   */
  countDayInterventions(date: Date, technician: UserModel | null): number {
    if (
      !technician ||
      !date ||
      !this.currentEvents ||
      this.currentEvents.length === 0
    ) {
      return 0
    }

    return this.currentEvents.filter((event) => {
      // Vérifier si l'événement a lieu à la date spécifiée
      const eventDate = new Date(event.start)
      const isSameDay = this.isSameDay(eventDate, date)

      if (!isSameDay) return false

      // Vérifier si c'est un événement créé par l'utilisateur
      const isUserCreated =
        event.isUserCreated === true ||
        event.classNames?.includes('user-created-event') ||
        (Array.isArray(event.classNames) &&
          event.classNames.some((c: string) => c === 'user-created-event'))

      // Les événements créés par l'utilisateur sont toujours comptabilisés pour l'itinéraire
      if (isUserCreated) {
        return true
      }

      // Vérifier si l'événement est associé au technicien
      let isForTechnician = false

      // Vérifier dans data.usersTechnicians (format API)
      if (
        event.data &&
        event.data.usersTechnicians &&
        Array.isArray(event.data.usersTechnicians)
      ) {
        isForTechnician = event.data.usersTechnicians.some(
          (tech: any) =>
            tech.email === technician.email || tech.id === technician.id
        )
      }

      // Vérifier dans usersTechnicians direct (format interne)
      if (
        !isForTechnician &&
        event.usersTechnicians &&
        Array.isArray(event.usersTechnicians)
      ) {
        isForTechnician = event.usersTechnicians.some(
          (tech: any) =>
            tech.email === technician.email || tech.id === technician.id
        )
      }

      return isSameDay && isForTechnician
    }).length
  }

  /**
   * Vérifie si un jour spécifique a des interventions pour un technicien donné
   * @param date La date à vérifier
   * @param technician Le technicien à vérifier
   * @returns true si des interventions existent, false sinon
   */
  dayHasInterventions(date: Date, technician: UserModel | null): boolean {
    if (
      !technician ||
      !date ||
      !this.currentEvents ||
      this.currentEvents.length === 0
    ) {
      return false
    }

    return this.currentEvents.some((event) => {
      // Vérifier si l'événement a lieu à la date spécifiée
      const eventDate = new Date(event.start)
      const isSameDay = this.isSameDay(eventDate, date)

      if (!isSameDay) return false

      // Vérifier si c'est un événement créé par l'utilisateur
      const isUserCreated =
        event.isUserCreated === true ||
        event.classNames?.includes('user-created-event') ||
        (Array.isArray(event.classNames) &&
          event.classNames.some((c: string) => c === 'user-created-event'))

      // Les événements créés par l'utilisateur sont toujours valides pour l'itinéraire
      if (isUserCreated) {
        return true
      }

      // Vérifier si l'événement est associé au technicien
      let isForTechnician = false

      // Vérifier dans data.usersTechnicians (format API)
      if (
        event.data &&
        event.data.usersTechnicians &&
        Array.isArray(event.data.usersTechnicians)
      ) {
        isForTechnician = event.data.usersTechnicians.some(
          (tech: any) =>
            tech.email === technician.email || tech.id === technician.id
        )
      }

      // Vérifier dans usersTechnicians direct (format interne)
      if (
        !isForTechnician &&
        event.usersTechnicians &&
        Array.isArray(event.usersTechnicians)
      ) {
        isForTechnician = event.usersTechnicians.some(
          (tech: any) =>
            tech.email === technician.email || tech.id === technician.id
        )
      }

      return isSameDay && isForTechnician
    })
  }

  /**
   * Ouvre l'URL de l'itinéraire journalier dans un nouvel onglet
   * @param technician Le technicien pour l'itinéraire
   * @param date La date pour laquelle générer l'itinéraire
   */
  openDailyRouteUrl(technician: UserModel | any, date: Date): void {
    // Vérifier si le jour contient des interventions
    if (!this.dayHasInterventions(date, technician)) {
      console.warn("Pas d'interventions pour ce jour et ce technicien")
      alert(
        "Aucune intervention n'est programmée pour ce technicien à cette date"
      )
      return
    }

    if (
      !technician ||
      !date ||
      !this.currentEvents ||
      this.currentEvents.length === 0
    ) {
      console.error("Données manquantes pour générer l'URL d'itinéraire", {
        technician,
        date,
        eventsCount: this.currentEvents?.length || 0,
      })

      alert(
        "Impossible de générer l'itinéraire. Veuillez vérifier que le technicien a bien des interventions programmées à cette date."
      )
      return
    }

    const url = this.generateDailyRouteUrl(technician, date)

    if (!url || url === 'https://www.google.com/maps/dir/') {
      console.error("URL d'itinéraire invalide générée")
      alert('Aucune intervention trouvée pour ce technicien à cette date')
      return
    }

    window.open(url, '_blank')
  }

  /**
   * Génération d'URL de routing journalier pour un technicien
   * Inclut les événements créés par l'utilisateur avec l'adresse de l'affaire courante
   * Les waypoints sont triés par heure de début des événements
   * @param technician Le technicien pour l'itinéraire
   * @param date La date pour laquelle générer l'itinéraire
   * @returns URL de l'itinéraire Google Maps
   */
  generateDailyRouteUrl(technician: UserModel | any, date: Date): string {
    if (!technician || !date || !this.currentEvents) {
      return ''
    }

    const dateString = DateTools.formatDateForAPI(date)

    // Filtrer les événements pour ce jour
    const eventsForDay = this.currentEvents.filter((event) => {
      const eventDate = new Date(event.start)
      const eventDateString = DateTools.formatDateForAPI(eventDate)
      return eventDateString === dateString
    })

    if (eventsForDay.length === 0) {
      return ''
    }

    // Construire l'URL
    let mapsUrl = 'https://www.google.com/maps/dir/?api=1'

    // Adresse du technicien comme origine et destination
    const technicianAddress = encodeURIComponent(
      [technician.postalCode || '', technician.locality || '']
        .filter(Boolean)
        .join(', ')
    )

    mapsUrl += `&origin=${technicianAddress}`
    mapsUrl += `&destination=${technicianAddress}`

    // Construire les waypoints avec leur timestamp pour le tri
    const waypointsWithTime: { address: string; startTime: number }[] = []

    eventsForDay.forEach((event) => {
      const eventStartTime = new Date(event.start).getTime()

      // Vérifier si c'est un événement créé par l'utilisateur
      const isUserCreated =
        event.isUserCreated === true ||
        event.classNames?.includes('user-created-event') ||
        (Array.isArray(event.classNames) &&
          event.classNames.some((c: string) => c === 'user-created-event'))

      if (isUserCreated) {
        // Pour les événements créés par l'utilisateur, utiliser l'adresse de l'affaire
        if (this.affair) {
          const affairAddress = [
            this.affair.workSitePostalCode || '',
            this.affair.workSiteLocality || '',
          ]
            .filter(Boolean)
            .join(', ')

          if (affairAddress) {
            waypointsWithTime.push({
              address: encodeURIComponent(affairAddress),
              startTime: eventStartTime,
            })
          }
        }
      } else if (event.data && event.data.resource === 'intervention') {
        // Pour les interventions existantes, vérifier si le technicien est assigné
        const hasTechnician = event.data.usersTechnicians?.some(
          (tech: any) =>
            tech.email === technician.email || tech.id === technician.id
        )

        if (hasTechnician) {
          const address = [
            event.data.workSitePostalCode || '',
            event.data.workSiteLocality || '',
          ]
            .filter(Boolean)
            .join(', ')

          if (address) {
            waypointsWithTime.push({
              address: encodeURIComponent(address),
              startTime: eventStartTime,
            })
          }
        }
      }
    })

    // Trier les waypoints par heure de début
    waypointsWithTime.sort((a, b) => a.startTime - b.startTime)

    // Extraire les adresses triées
    const waypoints = waypointsWithTime.map((w) => w.address)

    if (waypoints.length > 0) {
      mapsUrl += '&waypoints=' + waypoints.join('|')
    }

    mapsUrl += '&travelmode=driving'

    return mapsUrl
  }

  /**
   * Récupère tous les jours de la période actuelle affichée dans le calendrier
   * @returns Un tableau d'objets contenant les informations sur chaque jour
   */
  getWeekDays(): {
    date: Date
    shortName: string
    longFormat: string
    isToday: boolean
    hasInterventions: boolean
    interventionCount: number
  }[] {
    const result = []
    const today = new Date()

    // Utiliser les dates du calendrier actuel, avec un maximum de 7 jours
    let startDate = new Date(this.currentCalendarRange.start)
    let endDate = new Date(this.currentCalendarRange.end)

    // Limiter à 7 jours maximum
    const maxDays = 7
    const totalDays = Math.min(
      maxDays,
      Math.ceil(
        (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
      )
    )

    // Si nous sommes en vue mois, montrer les 7 jours à partir d'aujourd'hui
    if (this.currentView === 'dayGridMonth') {
      startDate = new Date()
    }

    // Générer les jours
    for (let i = 0; i < totalDays; i++) {
      const date = new Date(startDate)
      date.setDate(startDate.getDate() + i)

      // Formater le nom court du jour (L, M, M, J, V, S, D)
      const shortName = this.getDayShortName(date.getDay())

      // Format long pour le tooltip (ex: "Lundi 28 mars")
      const longFormat = this.formatDayForTooltip(date)

      // Vérifier si c'est aujourd'hui
      const isToday = this.isSameDay(date, today)

      const technicianToCheck =
        this.selectedTechForRoutes ||
        (this.selectedTechnicians.length === 1
          ? this.selectedTechnicians[0]
          : null)

      // Compter le nombre d'interventions pour ce jour
      const interventionCount = this.countDayInterventions(
        date,
        technicianToCheck
      )
      const hasInterventions = interventionCount > 0

      result.push({
        date,
        shortName,
        longFormat,
        isToday,
        hasInterventions,
        interventionCount,
      })
    }

    return result
  }

  /**
   * Récupère le nom court du jour de la semaine
   * @param dayIndex L'index du jour (0 = dimanche, 1 = lundi, etc.)
   * @returns Le nom court du jour
   */
  private getDayShortName(dayIndex: number): string {
    const shortNames = ['D', 'L', 'M', 'M', 'J', 'V', 'S']
    return shortNames[dayIndex]
  }

  /**
   * Formate la date pour l'affichage dans le tooltip
   * @param date La date à formater
   * @returns La date formatée (ex: "Lundi 28 mars")
   */
  private formatDayForTooltip(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }
    return date.toLocaleDateString('fr-FR', options)
  }

  /**
   * Vérifie si deux dates sont le même jour
   * @param date1 Première date
   * @param date2 Deuxième date
   * @returns true si les deux dates sont le même jour
   */
  private isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    )
  }

  /**
   * Sélectionne un technicien pour afficher ses itinéraires
   * @param technician Le technicien à sélectionner
   */
  selectTechnicianForRoutes(technician: UserModel): void {
    this.selectedTechForRoutes = technician
  }

  /**
   * Change la vue du calendrier
   * @param viewName Le nom de la vue à activer (dayGridMonth, timeGridWeek, timeGridDay)
   */
  changeCalendarView(viewName: string): void {
    if (!this.calendar?.getApi()) return

    this.calendar.getApi().changeView(viewName)
    this.currentView = viewName

    // Mettre à jour les jours affichés lorsque la vue change
    this.cdr.detectChanges()
  }

  /**
   * Navigue vers une date spécifique dans le calendrier
   * @param date La date à afficher
   */
  goToDate(date: Date): void {
    if (!this.calendar?.getApi()) return

    this.calendar.getApi().gotoDate(date)

    // Si on est en vue mois, passer en vue jour pour cette date
    if (this.currentView === 'dayGridMonth') {
      this.changeCalendarView('timeGridDay')
    }
  }

  // Méthode appelée par le template pour le menu contextuel
  onCalendarContextMenu(event: MouseEvent) {
    if (this.selectedEvent && this.selectedEvent.extendedProps?.productsData) {
      event.preventDefault()
      this.contextMenu?.show(event)
    }
  }

  /**
   * Applique un créneau pré-sélectionné depuis l'étape précédente au calendrier
   * @param preSelectedSlotData Les données du créneau pré-sélectionné
   */
  applyPreSelectedSlot(preSelectedSlotData: any): void {
    console.log('applyPreSelectedSlot appelé avec:', preSelectedSlotData)

    if (
      !preSelectedSlotData ||
      !preSelectedSlotData.slot ||
      !preSelectedSlotData.technician
    ) {
      console.log('applyPreSelectedSlot: Données de créneau invalides, retour')
      return
    }

    const slot = preSelectedSlotData.slot
    const technician = preSelectedSlotData.technician

    // Utiliser les produits sélectionnés à l'étape 1
    if (!this.selectedProducts || this.selectedProducts.length === 0) {
      console.log('applyPreSelectedSlot: Aucun produit sélectionné, retour')
      return
    }

    // Vérifier si le calendrier est prêt
    if (!this.calendar?.getApi()) {
      console.log('applyPreSelectedSlot: Calendrier non prêt, retour')
      return
    }

    const api = this.calendar.getApi()
    const existingEvents = api.getEvents()

    // Vérifier si un événement avec ces données pré-sélectionnées existe déjà
    const alreadyExists = existingEvents.some(
      (event) =>
        (event.extendedProps?.['isPreSelected'] &&
          event.extendedProps?.['preSelectedSlot']?.start === slot.start) ||
        (event.classNames?.includes('preselected-event') &&
          event.start?.getTime() === new Date(slot.start).getTime())
    )

    if (alreadyExists) {
      console.log('applyPreSelectedSlot: Événement déjà existant, retour')
      return
    }

    // Calculer la durée totale des produits
    const totalDurationMinutes =
      this.selectedProducts.reduce(
        (sum: number, product: any) => sum + (product.product?.duration || 0),
        0
      ) || 120

    // Créer le nom des produits
    const productsNames = this.selectedProducts
      .map((p: any) => p.product?.name || 'Produit')
      .join(', ')

    // S'assurer que le technicien est sélectionné
    if (!this.selectedTechnicians.find((t) => t.id === technician.id)) {
      this.selectedTechnicians.push(technician)
      this.usersForm.get('users')?.setValue(this.selectedTechnicians)
    }

    // Créer un événement sur le calendrier au moment pré-sélectionné
    const startDate = new Date(slot.start)

    // Calculer la date de fin correcte basée sur la durée des produits
    const endDate = new Date(
      startDate.getTime() + totalDurationMinutes * 60 * 1000
    )

    // Obtenir la source ERP
    const erpSource = this.getDefaultErpSource()

    // Préparer les données des produits comme pour le drag-and-drop
    const productsData = this.selectedProducts.map((product: any) => ({
      ...product,
      erpSource: erpSource,
    }))

    // Ajouter les IDs de produit à la liste des programmés
    this.addProductsToScheduled(productsData)

    // Générer un ID unique pour l'événement
    const productIds = productsData.map((p: any) => p.id) || []
    const eventId = `preselected_${Date.now()}`

    // Créer l'événement directement via l'API FullCalendar
    const newEvent = {
      id: eventId,
      title: productsNames,
      start: startDate,
      end: endDate,
      classNames: ['user-created-event', 'preselected-event'],
      editable: true,
      extendedProps: {
        productsData: productsData,
        totalDurationMinutes: totalDurationMinutes,
        isPreSelected: true,
        preSelectedSlot: slot,
        preSelectedTechnician: technician,
        preserveOnReload: true,
        uniqueId: eventId,
      },
    }

    // Ajouter l'événement au calendrier
    api.addEvent(newEvent)

    // Ajouter l'événement créé à currentEvents pour le calcul de l'itinéraire
    const newEventData = {
      id: eventId,
      title: productsNames,
      start: startDate,
      end: endDate,
      classNames: ['user-created-event', 'preselected-event'],
      isUserCreated: true,
      productsData: productsData,
      extendedProps: {
        productsData: productsData,
        uniqueId: eventId,
      },
    }
    this.currentEvents.push(newEventData)

    // Mettre à jour l'état
    this.hasDropEvent = true
    this.hasSingleProductDropped = productsData.length === 1

    // Créer l'objet de détails de dépôt
    const dropDetails = {
      title: productsNames,
      date: startDate,
      productsData: productsData,
      duration: totalDurationMinutes,
      end: endDate,
      erpSource: erpSource,
      isProductGroup: productsData.length > 1,
      isAlreadyInCalendar: true,
      uniqueId: eventId,
    }

    // Émettre les détails de dépôt
    this.ngZone.run(() => {
      this.dropDetails.emit(dropDetails)
      this.cdr.detectChanges()
    })

    // Naviguer vers la date de l'événement
    api.gotoDate(startDate)

    // Définir la vue appropriée
    if (this.currentView === 'dayGridMonth') {
      api.changeView('timeGridWeek')
      this.currentView = 'timeGridWeek'
    }

    // Notifier l'utilisateur que l'itinéraire a été recalculé
    this.notifyRouteRecalculated()

    console.log(
      `Créneau pré-sélectionné appliqué: ${slot.start} - ${slot.end} pour ${technician.firstname} ${technician.lastname}`
    )
  }

  /**
   * Handle click on calendar to detect week number clicks
   * @param event The click event
   */
  onCalendarClick(event: MouseEvent): void {
    const target = event.target as HTMLElement

    // Check if the clicked element is a week number cell
    if (
      target.classList.contains('p-datepicker-weeknumber') ||
      target.closest('.p-datepicker-weeknumber')
    ) {
      // Find the row that was clicked
      const row = target.closest('tr')
      if (row) {
        // Get the current month and year from the DatePicker component
        const viewMonth =
          this.weekPickerCalendar?.currentMonth ?? new Date().getMonth()
        const viewYear =
          this.weekPickerCalendar?.currentYear ?? new Date().getFullYear()

        // Get all day cells in the row (excluding week number cell)
        const dayCells = row.querySelectorAll(
          'td:not(.p-datepicker-weeknumber)'
        )

        if (dayCells.length === 7) {
          const weekDates: Date[] = []

          dayCells.forEach((cell, index) => {
            const span = cell.querySelector('span')
            if (span) {
              const dayNum = parseInt(span.textContent || '0', 10)
              if (dayNum > 0) {
                let cellMonth = viewMonth
                let cellYear = viewYear

                // Check if this cell is from another month by looking at the TD element
                const isOtherMonth = cell.classList.contains(
                  'p-datepicker-other-month'
                )

                if (isOtherMonth) {
                  // Use position in the row to determine if it's previous or next month
                  // Days at the beginning of the row (index 0-2) with high day numbers = previous month
                  // Days at the end of the row (index 4-6) with low day numbers = next month
                  if (index < 3 && dayNum > 20) {
                    // Previous month
                    cellMonth = viewMonth - 1
                    if (cellMonth < 0) {
                      cellMonth = 11
                      cellYear = viewYear - 1
                    }
                  } else if (index > 3 && dayNum < 10) {
                    // Next month
                    cellMonth = viewMonth + 1
                    if (cellMonth > 11) {
                      cellMonth = 0
                      cellYear = viewYear + 1
                    }
                  }
                }

                weekDates.push(new Date(cellYear, cellMonth, dayNum))
              }
            }
          })

          if (weekDates.length > 0) {
            // Get the first and last dates of the week
            const startDate = weekDates[0]
            const endDate = weekDates[weekDates.length - 1]

            // Set the date range
            this.rangeDates.setValue([startDate, endDate])
          }
        }
      }
    }
  }
}
